import { type Ref, ref } from 'vue'

import { useConfig } from '@/composables/config'
import { loadScript } from '@/utils/loadScript'

const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
const TURNSTILE_SCRIPT_TIMEOUT = 10000

type UseTurnstileOptions = {
    onSuccess?: (token: string) => void
    onError?: () => void
}

export const useTurnstile = (options: UseTurnstileOptions = {}) => {
    const { VUE_APP_TURNSTILE_SITE_KEY } = useConfig()

    const widgetId = ref<string>()
    const token = ref<string>()

    const render = async (container: Ref<HTMLElement | null>) => {
        if (!VUE_APP_TURNSTILE_SITE_KEY) {
            return
        }

        try {
            await loadScript({
                src: TURNSTILE_SCRIPT_URL,
                async: true,
                timeoutMs: TURNSTILE_SCRIPT_TIMEOUT,
                useExisting: true,
            })
        } catch (error) {
            console.error('Failed to load Turnstile script:', error)
        }

        if (!container?.value || !window.turnstile) {
            return
        }

        if (widgetId.value) {
            remove()
        }

        try {
            widgetId.value = window.turnstile.render(container.value, {
                sitekey: VUE_APP_TURNSTILE_SITE_KEY,
                callback: (receivedToken: string) => {
                    token.value = receivedToken
                    options.onSuccess?.(receivedToken)
                },
                'error-callback': () => {
                    token.value = undefined
                    options.onError?.()
                },
                theme: 'dark',
            })
        } catch (error) {
            console.error('Failed to render Turnstile widget:', error)
        }
    }

    const remove = () => {
        if (widgetId.value && window.turnstile) {
            try {
                window.turnstile.remove(widgetId.value)
                widgetId.value = undefined
                token.value = undefined
            } catch (error) {
                console.error('Failed to remove Turnstile widget:', error)
            }
        }
    }

    return {
        render,
        remove,
    }
}
